import { Line } from "react-chartjs-2";
import {
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartOptions,
} from "chart.js";
import { useIsDarkMode } from "~/hooks/useIsDarkMode";
import { CHART_AXIS_TEXT_COLOR, CHART_GRID_COLOR, CHART_SERIES_COLOR } from "~/lib/chartPalette";
import { cn } from "~/lib/utils";
import { EVAL_CHART_HEIGHT_CLS } from "./constants/charts";
import { formatDuration } from "./primitives/format";
import type { RunTimePoint } from "./selectors/runs";

// Registered at module load so the elements exist before the chart mounts
// (child chart effects run before a parent page effect would).
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const chartOptions = (
  tooltipTitles: string[],
  gridColor: string,
  axisTextColor: string,
  points: RunTimePoint[]
): ChartOptions<"line"> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  // The hover target is the whole column, not the 8px dot.
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      displayColors: false,
      callbacks: {
        title: (items) => tooltipTitles[items[0].dataIndex],
        // Read off the source point rather than the chart item, so the figure is
        // formatted the way every other duration on the page is.
        label: (item) => formatDuration(points[item.dataIndex].durationMinutes * 60),
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { color: gridColor },
      ticks: { color: axisTextColor, font: { size: 11 }, maxRotation: 0, autoSkipPadding: 16 },
    },
    y: {
      // From zero: a clipped axis would turn a two-minute wobble on a half-hour
      // run into a cliff.
      beginAtZero: true,
      ticks: {
        color: axisTextColor,
        font: { size: 11 },
        callback: (value) => `${value} min`,
      },
      grid: { color: gridColor, tickLength: 0 },
      border: { display: false },
    },
  },
});

/**
 * Wall-clock minutes per completed run, oldest left. One line and no spread band:
 * a run has one duration, where the score chart has a range of case scores to
 * draw behind its mean. Runs still going are absent rather than plotted as 0.
 */
export const RunTimeTrend = ({ points }: { points: RunTimePoint[] }) => {
  const isDark = useIsDarkMode();
  const mode = isDark ? "dark" : "light";
  const lineColor = CHART_SERIES_COLOR[mode];
  const gridColor = CHART_GRID_COLOR[mode];
  const axisTextColor = CHART_AXIS_TEXT_COLOR[mode];
  const surfaceColor = isDark ? "#212121" : "#ffffff";

  // One tick per day, as the score chart does, so the two read against the same
  // dates.
  const dayLabels = points.map((point) =>
    new Date(point.startedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
  );
  const axisLabels = dayLabels.map((day, index) => (day === dayLabels[index - 1] ? "" : day));
  const tooltipTitles = points.map((point) => new Date(point.startedAt).toLocaleString());

  if (points.length < 2) {
    return (
      <div
        className={cn(
          EVAL_CHART_HEIGHT_CLS,
          "flex items-center justify-center text-sm text-muted-foreground"
        )}
      >
        {points.length === 0
          ? "No completed runs in this window yet."
          : "Not enough completed runs yet — a trend needs at least two completed runs."}
      </div>
    );
  }

  return (
    <div className={EVAL_CHART_HEIGHT_CLS}>
      <Line
        data={{
          labels: axisLabels,
          datasets: [
            {
              label: "Run time",
              data: points.map((point) => point.durationMinutes),
              borderColor: lineColor,
              borderWidth: 2,
              tension: 0.25,
              fill: false,
              pointBackgroundColor: lineColor,
              pointBorderColor: surfaceColor,
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
            },
          ],
        }}
        options={chartOptions(tooltipTitles, gridColor, axisTextColor, points)}
      />
    </div>
  );
};
